/* ==========================================================================
   Inner Stride — theme.js
   Vanilla JS. No build step required. Organized by feature, all delegated
   from `document` so it keeps working after AJAX content swaps (cart drawer,
   quick add modal, collection grid, etc).
   ========================================================================== */
(function () {
  'use strict';

  var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  window.InnerStride = window.InnerStride || {};

  /* ---------------------------------------------------------------------
     Utilities
     --------------------------------------------------------------------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function announce(message) {
    var region = qs('#a11y-live-region');
    if (region) region.textContent = message;
  }
  function trapFocus(container) {
    var focusable = qsa('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', container);
    if (focusable.length) focusable[0].focus();
  }
  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error('Request failed: ' + url);
      return res.json();
    });
  }
  function parseHTML(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc;
  }
  function moneyFormat(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments, ctx = this;
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* ---------------------------------------------------------------------
     Header scroll state (transparent -> solid)
     --------------------------------------------------------------------- */
  (function headerScroll() {
    var header = qs('[data-site-header]');
    if (!header || !header.classList.contains('site-header--transparent')) return;
    function onScroll() {
      header.classList.toggle('is-scrolled', window.scrollY > 40);
    }
    window.addEventListener('scroll', debounce(onScroll, 10), { passive: true });
    onScroll();
  })();

  /* ---------------------------------------------------------------------
     Mobile menu
     --------------------------------------------------------------------- */
  (function mobileMenu() {
    var menu = qs('[data-mobile-menu]');
    var overlay = qs('[data-menu-overlay]');
    if (!menu || !overlay) return;

    function open() {
      menu.hidden = false;
      overlay.hidden = false;
      requestAnimationFrame(function () {
        menu.classList.add('is-open');
        overlay.classList.add('is-open');
      });
      document.body.style.overflow = 'hidden';
      trapFocus(menu);
    }
    function close() {
      menu.classList.remove('is-open');
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(function () { menu.hidden = true; overlay.hidden = true; }, 300);
    }
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-menu-toggle]')) { open(); }
      if (e.target.closest('[data-menu-close]') || e.target === overlay) { close(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  })();

  /* ---------------------------------------------------------------------
     Generic overlay drawers/modals: cart, search, quick add, size guide
     --------------------------------------------------------------------- */
  function makeDrawer(selector, openTriggerSel, closeTriggerSel, onOpen) {
    var el = qs(selector);
    if (!el) return null;
    function open() {
      el.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (onOpen) onOpen(el);
      trapFocus(el);
    }
    function close() {
      el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
    document.addEventListener('click', function (e) {
      if (e.target.closest(openTriggerSel)) { e.preventDefault(); open(); }
      if (e.target.closest(closeTriggerSel)) { close(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.getAttribute('aria-hidden') === 'false') close();
    });
    return { open: open, close: close, el: el };
  }

  var cartDrawer = makeDrawer('[data-cart-drawer]', '[data-cart-open]', '[data-cart-close]');
  var searchDrawer = makeDrawer('[data-search-drawer]', '[data-search-open]', '[data-search-close]', function (el) {
    var input = qs('[data-search-input]', el);
    if (input) setTimeout(function () { input.focus(); }, 100);
  });
  var quickAddModal = makeDrawer('[data-quick-add-modal]', '[data-quick-add-open]', '[data-quick-add-close]');
  var sizeGuideModal = makeDrawer('[data-size-guide-modal]', '[data-size-guide-open]', '[data-size-guide-close]');

  window.InnerStride.openCartDrawer = cartDrawer ? cartDrawer.open : function () {};

  /* ---------------------------------------------------------------------
     Cart: AJAX add / update / remove, shared by drawer + page + quick add
     --------------------------------------------------------------------- */
  var CartAPI = {
    add: function (id, quantity, otherItems) {
      var items = otherItems || [{ id: id, quantity: quantity || 1 }];
      return fetchJSON(routes + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      });
    },
    changeByKey: function (key, quantity) {
      return fetchJSON(routes + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
    },
    updateNote: function (note) {
      return fetchJSON(routes + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note })
      });
    },
    get: function () {
      return fetchJSON(routes + 'cart.js');
    }
  };
  window.InnerStride.cart = CartAPI;

  function refreshCartDrawer() {
    return fetch(routes + 'cart?sections=cart-drawer')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data['cart-drawer']) {
          var doc = parseHTML(data['cart-drawer']);
          var freshDrawer = doc.querySelector('[data-cart-drawer]');
          var currentDrawer = qs('[data-cart-drawer]');
          if (freshDrawer && currentDrawer) {
            var wasOpen = currentDrawer.getAttribute('aria-hidden') === 'false';
            currentDrawer.innerHTML = freshDrawer.innerHTML;
            currentDrawer.setAttribute('aria-hidden', wasOpen ? 'false' : 'true');
          }
        }
        updateCartCountFromCart();
      });
  }

  function updateCartCountFromCart() {
    CartAPI.get().then(function (cart) {
      qsa('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
    });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-product-form], [data-quick-add-form]');
    if (!form) return;
    e.preventDefault();
    var submitBtn = qs('[data-add-to-cart-submit], [data-quick-add-submit]', form);
    var addText = qs('[data-add-text]', form);
    var formData = new FormData(form);

    if (submitBtn) submitBtn.disabled = true;

    fetchJSON(routes + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    }).then(function () {
      if (addText) addText.textContent = 'Added';
      announce('Item added to your bag');
      return refreshCartDrawer();
    }).then(function () {
      if (cartDrawer) cartDrawer.open();
    }).catch(function () {
      announce('Could not add item to bag');
    }).finally(function () {
      if (submitBtn) submitBtn.disabled = false;
      if (addText) setTimeout(function () { addText.textContent = addText.dataset.original || addText.textContent; }, 2000);
    });
  });

  // Quantity steppers (delegated) — used in cart drawer, cart page, product page, quick add
  document.addEventListener('click', function (e) {
    var decrease = e.target.closest('[data-quantity-decrease]');
    var increase = e.target.closest('[data-quantity-increase]');
    if (!decrease && !increase) return;
    var wrap = e.target.closest('.quantity-selector');
    var input = qs('[data-quantity-input]', wrap);
    if (!input) return;
    var value = parseInt(input.value, 10) || 1;
    var min = parseInt(input.getAttribute('min') || '0', 10);
    value = increase ? value + 1 : Math.max(min, value - 1);
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Cart line quantity change / remove (drawer + page)
  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-quantity-input]')) return;
    var row = e.target.closest('[data-cart-item]');
    if (!row) return; // not a cart row (e.g. product form quantity) — ignore
    var key = row.getAttribute('data-key');
    var qty = parseInt(e.target.value, 10) || 0;
    CartAPI.changeByKey(key, qty).then(function () { return refreshCartDrawer(); });
  });

  document.addEventListener('click', function (e) {
    var removeBtn = e.target.closest('[data-cart-remove]');
    if (!removeBtn) return;
    var row = removeBtn.closest('[data-cart-item]');
    if (!row) return;
    CartAPI.changeByKey(row.getAttribute('data-key'), 0).then(function () { return refreshCartDrawer(); });
  });

  // Cart note (drawer)
  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-cart-note]')) return;
    CartAPI.updateNote(e.target.value);
  });

  // Discount code -> deep link into checkout (carts can't preview discount math client-side)
  document.addEventListener('click', function (e) {
    var applyBtn = e.target.closest('[data-discount-apply]');
    if (!applyBtn) return;
    var wrap = applyBtn.closest('[data-discount-form]');
    var input = qs('[data-discount-input]', wrap);
    var code = input && input.value.trim();
    if (!code) return;
    window.location.href = routes + 'discount/' + encodeURIComponent(code) + '?redirect=/checkout';
  });

  /* ---------------------------------------------------------------------
     Quick add modal: fetch section content for a product handle
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-add-open]');
    if (!trigger) return;
    var handle = trigger.getAttribute('data-product-handle');
    var content = qs('[data-quick-add-content]');
    if (!content || !handle) return;
    content.innerHTML = '<div class="quick-add-modal__loading">Loading...</div>';
    fetch(routes + 'products/' + handle + '?section_id=quick-add-content')
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = parseHTML(html);
        var wrap = doc.querySelector('[data-section-type="quick-add"]');
        content.innerHTML = wrap ? wrap.outerHTML : html;
      });
  });

  /* ---------------------------------------------------------------------
     Variant picker: match selected options to a variant, update UI
     --------------------------------------------------------------------- */
  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-option-value]')) return;
    var picker = e.target.closest('[data-variant-picker]');
    if (!picker) return;
    updateVariantSelection(picker);
  });

  function updateVariantSelection(picker) {
    var productJSON = qs('[data-product-json]', picker);
    if (!productJSON) return;
    var product = JSON.parse(productJSON.textContent);
    var selectedValues = [];
    qsa('.variant-picker__option', picker).forEach(function (fieldset) {
      var checked = qs('input[data-option-value]:checked', fieldset);
      selectedValues.push(checked ? checked.value : null);
      var display = qs('[data-selected-value]', fieldset);
      if (display && checked) display.textContent = checked.value;
    });

    var matched = product.variants.find(function (v) {
      return v.options.every(function (opt, i) { return opt === selectedValues[i]; });
    });

    var select = qs('[data-variant-select]', picker);
    var form = picker.closest('form');
    var hiddenId = form ? qs('input[name="id"]', form) : null;
    var submitBtn = form ? qs('[data-add-to-cart-submit], [data-quick-add-submit]', form) : null;
    var addText = form ? qs('[data-add-text]', form) : null;

    if (matched) {
      if (select) select.value = matched.id;
      if (hiddenId) hiddenId.value = matched.id;
      if (submitBtn) submitBtn.disabled = !matched.available;
      if (addText) addText.textContent = matched.available ? (addText.dataset.addLabel || 'Add to Bag') : 'Sold Out';

      // Update price display within the same info panel
      var infoPanel = picker.closest('.product-page__info, .quick-add__info');
      if (infoPanel) {
        var priceAmount = qs('.price__amount', infoPanel);
        if (priceAmount) priceAmount.textContent = moneyFormat(matched.price);
      }

      // Swap main gallery image to the variant's featured image, if present
      if (matched.featured_image) {
        var gallery = qs('[data-product-gallery]');
        if (gallery) {
          var slide = qs('[data-gallery-slide][data-media-id="' + matched.featured_image.id + '"]', gallery);
          if (slide) {
            var thumb = qs('[data-gallery-thumb][data-media-id="' + matched.featured_image.id + '"]', gallery);
            if (thumb) thumb.click();
          }
        }
      }

      announce(matched.title + ' selected');
    } else if (submitBtn) {
      submitBtn.disabled = true;
      if (addText) addText.textContent = 'Unavailable';
    }
  }

  /* ---------------------------------------------------------------------
     Product gallery: thumbnails, arrows, lightbox zoom
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-gallery-thumb]');
    if (thumb) {
      var gallery = thumb.closest('[data-product-gallery]');
      var mediaId = thumb.getAttribute('data-media-id');
      qsa('[data-gallery-thumb]', gallery).forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
      qsa('[data-gallery-slide]', gallery).forEach(function (s) {
        var match = s.getAttribute('data-media-id') === mediaId;
        s.hidden = !match;
        s.classList.toggle('is-active', match);
      });
    }

    var next = e.target.closest('[data-gallery-next]');
    var prev = e.target.closest('[data-gallery-prev]');
    if (next || prev) {
      var g = e.target.closest('[data-product-gallery]');
      var thumbs = qsa('[data-gallery-thumb]', g);
      var activeIndex = thumbs.findIndex(function (t) { return t.classList.contains('is-active'); });
      var targetIndex = next ? (activeIndex + 1) % thumbs.length : (activeIndex - 1 + thumbs.length) % thumbs.length;
      if (thumbs[targetIndex]) thumbs[targetIndex].click();
    }

    var zoomTrigger = e.target.closest('[data-gallery-zoom]');
    if (zoomTrigger) {
      var lightbox = qs('[data-gallery-lightbox]');
      var img = qs('[data-gallery-lightbox-image]', lightbox);
      img.src = zoomTrigger.getAttribute('data-full-src');
      img.alt = '';
      lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
    }
    if (e.target.closest('[data-gallery-lightbox-close]') || e.target.matches('[data-gallery-lightbox]')) {
      var lb = qs('[data-gallery-lightbox]');
      if (lb) { lb.hidden = true; document.body.style.overflow = ''; }
    }
  });

  /* ---------------------------------------------------------------------
     Accordions (FAQ, product details)
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-accordion-trigger]');
    if (!trigger) return;
    var item = trigger.closest('.accordion__item');
    var panel = qs('[data-accordion-panel]', item);
    var expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  });

  /* ---------------------------------------------------------------------
     Carousels (reviews)
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var next = e.target.closest('[data-carousel-next]');
    var prev = e.target.closest('[data-carousel-prev]');
    if (!next && !prev) return;
    var carousel = e.target.closest('[data-carousel]');
    var track = qs('[data-carousel-track]', carousel);
    var amount = track.clientWidth * 0.85;
    track.scrollBy({ left: next ? amount : -amount, behavior: 'smooth' });
  });

  /* ---------------------------------------------------------------------
     Scroll reveal animations
     --------------------------------------------------------------------- */
  (function scrollReveal() {
    var items = qsa('[data-animate]');
    if (!items.length || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var delay = parseInt(entry.target.getAttribute('data-animate-delay') || '0', 10);
          setTimeout(function () { entry.target.classList.add('is-visible'); }, delay);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    items.forEach(function (el) { observer.observe(el); });
  })();

  /* ---------------------------------------------------------------------
     Related products / cart upsell: fetch Shopify recommendations endpoint
     --------------------------------------------------------------------- */
  function loadRecommendations(container) {
    var url = container.getAttribute('data-url');
    if (!url) return;
    fetch(url).then(function (res) { return res.text(); }).then(function (html) {
      var doc = parseHTML(html);
      var fresh = doc.querySelector('[data-related-products]') || doc.querySelector('[data-cart-upsell]');
      if (fresh && fresh.innerHTML.trim()) {
        container.innerHTML = fresh.innerHTML;
      } else {
        container.hidden = true;
      }
    }).catch(function () { container.hidden = true; });
  }
  document.addEventListener('DOMContentLoaded', function () {
    qsa('[data-related-products], [data-cart-upsell]').forEach(loadRecommendations);
  });

  /* ---------------------------------------------------------------------
     Recently viewed (localStorage, client-only)
     --------------------------------------------------------------------- */
  var RECENTLY_VIEWED_KEY = 'innerstride:recentlyViewed';

  window.InnerStride.trackRecentlyViewed = function (handle) {
    try {
      var list = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      list = list.filter(function (h) { return h !== handle; });
      list.unshift(handle);
      list = list.slice(0, 8);
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(list));
    } catch (err) { /* localStorage unavailable — skip silently */ }
  };

  document.addEventListener('DOMContentLoaded', function () {
    var block = qs('[data-recently-viewed]');
    if (!block) return;
    var exclude = block.getAttribute('data-exclude-handle');
    var list;
    try {
      list = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
    } catch (err) { list = []; }
    list = list.filter(function (h) { return h !== exclude; }).slice(0, 4);
    if (!list.length) return;

    var target = qs('[data-recently-viewed-list]', block);
    Promise.all(list.map(function (handle) {
      return fetch(routes + 'products/' + handle + '?section_id=quick-add-content')
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = parseHTML(html);
          var titleLink = doc.querySelector('.quick-add__title a');
          var img = doc.querySelector('.quick-add__media img');
          if (!titleLink) return null;
          return { url: titleLink.getAttribute('href'), title: titleLink.textContent, image: img ? img.getAttribute('src') : '' };
        }).catch(function () { return null; });
    })).then(function (products) {
      products.filter(Boolean).forEach(function (p) {
        var card = document.createElement('a');
        card.href = p.url;
        card.className = 'product-card';
        card.innerHTML = '<div class="product-card__media ratio ratio--portrait"><img src="' + p.image + '" alt="' + p.title + '" loading="lazy"></div><div class="product-card__info"><h3 class="product-card__title">' + p.title + '</h3></div>';
        target.appendChild(card);
      });
      if (products.filter(Boolean).length) block.hidden = false;
    });
  });

  /* ---------------------------------------------------------------------
     Sticky add-to-cart bar
     --------------------------------------------------------------------- */
  (function stickyATC() {
    var bar = qs('[data-sticky-atc]');
    var mainForm = qs('[data-product-form]');
    if (!bar || !mainForm) return;
    var submitBtn = qs('[data-add-to-cart-submit]', mainForm);

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { bar.classList.toggle('is-visible', !entry.isIntersecting); });
      }, { rootMargin: '-120px 0px 0px 0px' });
      if (submitBtn) observer.observe(submitBtn);
    }

    bar.addEventListener('click', function (e) {
      if (e.target.closest('[data-sticky-atc-submit]')) {
        mainForm.requestSubmit ? mainForm.requestSubmit() : mainForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    });
  })();

  /* ---------------------------------------------------------------------
     Frequently bought together: add multiple line items at once
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-fbt-add]');
    if (!btn) return;
    var wrap = btn.closest('[data-fbt]');
    var items = qsa('[data-fbt-checkbox]:checked', wrap).map(function (cb) {
      return { id: parseInt(cb.getAttribute('data-variant-id'), 10), quantity: 1 };
    });
    if (!items.length) return;
    btn.disabled = true;
    CartAPI.add(null, null, items).then(function () {
      announce('Items added to your bag');
      return refreshCartDrawer();
    }).then(function () {
      if (cartDrawer) cartDrawer.open();
    }).finally(function () { btn.disabled = false; });
  });

  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-fbt-checkbox]')) return;
    var wrap = e.target.closest('[data-fbt]');
    var total = qsa('[data-fbt-checkbox]:checked', wrap).reduce(function (sum, cb) {
      return sum + parseInt(cb.getAttribute('data-price'), 10);
    }, 0);
    var totalEl = qs('[data-fbt-total]', wrap);
    if (totalEl) totalEl.textContent = moneyFormat(total);
  });

  /* ---------------------------------------------------------------------
     Wishlist (localStorage-based; swap for a wishlist app's API if installed)
     --------------------------------------------------------------------- */
  var WISHLIST_KEY = 'innerstride:wishlist';
  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch (err) { return []; }
  }
  function paintWishlistButtons() {
    var wishlist = getWishlist();
    qsa('[data-wishlist-toggle]').forEach(function (btn) {
      var id = btn.getAttribute('data-product-id');
      var active = wishlist.indexOf(id) !== -1;
      btn.setAttribute('aria-pressed', String(active));
    });
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    var id = btn.getAttribute('data-product-id');
    var wishlist = getWishlist();
    var index = wishlist.indexOf(id);
    if (index === -1) { wishlist.push(id); announce('Added to wishlist'); }
    else { wishlist.splice(index, 1); announce('Removed from wishlist'); }
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist)); } catch (err) {}
    paintWishlistButtons();
  });
  document.addEventListener('DOMContentLoaded', paintWishlistButtons);

  /* ---------------------------------------------------------------------
     Collection filters, sorting, mobile drawer, load more
     --------------------------------------------------------------------- */
  (function collectionPage() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-filters-toggle]')) {
        var filters = qs('[data-collection-filters]');
        if (filters) filters.classList.toggle('is-open');
      }
    });

    document.addEventListener('change', function (e) {
      if (e.target.matches('[data-sort-select]')) {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', e.target.value);
        window.location.href = url.toString();
      }
      if (e.target.matches('[data-filter-input]')) {
        submitFilters();
      }
    });

    function submitFilters() {
      var form = qs('[data-filter-form]');
      if (!form) return;
      var params = new URLSearchParams(new FormData(form));
      var url = new URL(window.location.href);
      // Preserve sort_by, replace filter params
      var sortBy = url.searchParams.get('sort_by');
      var newParams = new URLSearchParams(params);
      if (sortBy) newParams.set('sort_by', sortBy);
      window.location.search = newParams.toString();
    }

    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-filters-clear]')) return;
      var url = new URL(window.location.href);
      window.location.href = url.pathname;
    });

    // Infinite scroll / load more
    var loadMoreBtn = qs('[data-load-more]');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        var pagination = qs('[data-collection-pagination]');
        var nextUrl = pagination.getAttribute('data-next-url');
        if (!nextUrl) return;
        loadMoreBtn.disabled = true;
        loadMoreBtn.textContent = 'Loading...';
        fetch(nextUrl).then(function (res) { return res.text(); }).then(function (html) {
          var doc = parseHTML(html);
          var newGrid = doc.querySelector('[data-collection-grid]');
          var newPagination = doc.querySelector('[data-collection-pagination]');
          var currentGrid = qs('[data-collection-grid]');
          if (newGrid && currentGrid) {
            Array.prototype.forEach.call(newGrid.children, function (child) { currentGrid.appendChild(child); });
            qsa('[data-animate]', currentGrid).forEach(function (el) { el.classList.add('is-visible'); });
          }
          if (newPagination) {
            pagination.setAttribute('data-next-url', newPagination.getAttribute('data-next-url') || '');
            var stillHasNext = newPagination.getAttribute('data-next-url');
            if (!stillHasNext) loadMoreBtn.remove();
          }
        }).finally(function () {
          if (loadMoreBtn.isConnected) { loadMoreBtn.disabled = false; loadMoreBtn.textContent = 'Load More'; }
        });
      });
    }
  })();

  /* ---------------------------------------------------------------------
     Predictive search
     --------------------------------------------------------------------- */
  (function predictiveSearch() {
    var input = qs('[data-search-input]');
    var results = qs('[data-search-results]');
    if (!input || !results) return;

    var handleInput = debounce(function () {
      var term = input.value.trim();
      if (term.length < 2) { results.innerHTML = ''; return; }
      fetch(routes + 'search/suggest.json?q=' + encodeURIComponent(term) + '&resources[type]=product&resources[limit]=6')
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (!products.length) {
            results.innerHTML = '<p class="search-drawer__empty">No results found for "' + term + '"</p>';
            return;
          }
          results.innerHTML = '<div class="product-grid product-grid--4-col">' + products.map(function (p) {
            return '<a class="product-card" href="' + p.url + '">' +
              '<div class="product-card__media ratio ratio--portrait"><img src="' + (p.featured_image ? p.featured_image.url : '') + '" alt="' + p.title + '" loading="lazy"></div>' +
              '<div class="product-card__info"><h3 class="product-card__title">' + p.title + '</h3><div class="price"><span class="price__amount">' + p.price + '</span></div></div>' +
              '</a>';
          }).join('') + '</div>';
        });
    }, 250);

    input.addEventListener('input', handleInput);
  })();

  /* ---------------------------------------------------------------------
     Recent activity toast (merchant-curated, see recent-sales-popup.liquid)
     --------------------------------------------------------------------- */
  (function recentSalesPopup() {
    var wrap = qs('[data-recent-sales-popup]');
    if (!wrap) return;
    var items = qsa('[data-recent-sales-item]', wrap);
    if (!items.length) return;
    var displayMs = parseInt(wrap.getAttribute('data-interval'), 10) || 6000;
    var rotateMs = parseInt(wrap.getAttribute('data-rotate'), 10) || 14000;
    var index = 0;
    var dismissed = false;

    function showNext() {
      if (dismissed) return;
      items.forEach(function (item) { item.hidden = true; item.classList.remove('is-visible'); });
      var current = items[index % items.length];
      current.hidden = false;
      requestAnimationFrame(function () { current.classList.add('is-visible'); });
      setTimeout(function () {
        current.classList.remove('is-visible');
        setTimeout(function () { current.hidden = true; }, 300);
      }, displayMs);
      index++;
    }

    wrap.addEventListener('click', function (e) {
      if (e.target.closest('[data-recent-sales-close]')) {
        dismissed = true;
        items.forEach(function (item) { item.hidden = true; });
      }
    });

    setTimeout(showNext, 3000);
    setInterval(showNext, rotateMs);
  })();

  /* ---------------------------------------------------------------------
     Customer login: reveal recover-password form on #recover
     --------------------------------------------------------------------- */
  (function recoverPasswordToggle() {
    var form = qs('.recover-password-form');
    if (!form) return;
    function sync() {
      if (window.location.hash === '#recover') form.classList.add('is-visible');
    }
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-recover-toggle]')) {
        e.preventDefault();
        form.classList.add('is-visible');
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    sync();
  })();

  /* ---------------------------------------------------------------------
     Address delete confirmation
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-confirm]');
    if (!btn) return;
    if (!window.confirm(btn.getAttribute('data-confirm'))) e.preventDefault();
  });

  /* ---------------------------------------------------------------------
     Exit-intent offer: desktop mouse-toward-tab-bar, mobile scroll+dwell
     fallback. Shows once per browser session.
     --------------------------------------------------------------------- */
  (function exitIntent() {
    var popup = qs('[data-exit-popup]');
    if (!popup) return;
    var SESSION_KEY = 'innerstride:exitIntentShown';
    var minDelayMs = (parseInt(popup.getAttribute('data-delay'), 10) || 0) * 1000;
    var siteEnteredAt = Date.now();
    var shown = false;

    function alreadyShown() {
      try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (err) { return false; }
    }
    function markShown() {
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (err) {}
    }
    function eligible() {
      return !shown && !alreadyShown() && (Date.now() - siteEnteredAt) >= minDelayMs;
    }
    function open() {
      if (!eligible()) return;
      shown = true;
      markShown();
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      trapFocus(popup);
    }
    function close() {
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    popup.addEventListener('click', function (e) {
      if (e.target.closest('[data-exit-popup-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.getAttribute('aria-hidden') === 'false') close();
    });

    // Desktop: cursor exits toward the browser chrome (address bar / tab close)
    document.addEventListener('mouseleave', function (e) {
      if (e.clientY <= 0) open();
    });

    // Mobile/touch fallback: no mouseleave signal exists, so use a generous
    // scroll-depth + dwell-time heuristic instead, capped to touch devices
    // so desktop visitors only ever see the mouse-based trigger.
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
      var handleScroll = debounce(function () {
        var scrollDepth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
        if (scrollDepth > 0.6) {
          open();
          window.removeEventListener('scroll', handleScroll);
        }
      }, 200);
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
  })();

  /* ---------------------------------------------------------------------
     Sale countdown: hides itself automatically once the merchant-set end
     time has passed, so it can never display a stale/misleading timer.
     --------------------------------------------------------------------- */
  (function saleCountdown() {
    var bar = qs('[data-sale-countdown]');
    if (!bar) return;
    var endTime = new Date(bar.getAttribute('data-end')).getTime();
    if (isNaN(endTime)) return;

    var days = qs('[data-countdown-days]', bar);
    var hours = qs('[data-countdown-hours]', bar);
    var minutes = qs('[data-countdown-minutes]', bar);
    var seconds = qs('[data-countdown-seconds]', bar);
    var intervalId;

    function pad(n) { return String(n).padStart(2, '0'); }

    function tick() {
      var remaining = endTime - Date.now();
      if (remaining <= 0) {
        bar.hidden = true;
        clearInterval(intervalId);
        return;
      }
      bar.hidden = false;
      var d = Math.floor(remaining / 86400000);
      var h = Math.floor((remaining % 86400000) / 3600000);
      var m = Math.floor((remaining % 3600000) / 60000);
      var s = Math.floor((remaining % 60000) / 1000);
      if (days) days.textContent = pad(d);
      if (hours) hours.textContent = pad(h);
      if (minutes) minutes.textContent = pad(m);
      if (seconds) seconds.textContent = pad(s);
    }

    tick();
    intervalId = setInterval(tick, 1000);
  })();

})();
